import { Grid, useEventCallback } from '@mui/material';
import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { TypeFile } from 'practice/components/detail/form/accordion-attachments/form';
import {
  AllegatoAtti,
  DocumentVM,
  LastStepVM,
  ParentVM,
  SectionVM,
} from 'civil-status/view-models/step-form';
import { DocumentWizard } from 'shell/models/document-wizard';
import {
  idAgeDefectDefault,
  idMunicipalityDifferentMilano,
  idOpposition2Default,
  idOppositionDefault,
  idRelationshipDefault,
  idTemporaryBanRemarriageDefault,
  typePresence,
} from 'civil-status/constants/type-presence';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { HttpResponse } from 'shell/models/http-response';
import { PmiButton } from 'shell/components/shared/button';
import { AxiosResponse } from 'axios';
import { AttachmentsData } from 'shell/models/act-wizard';
import FormData from './form-data';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { idStateDocumentWizard } from 'civil-status/constants/id-state-document-wizard';
import { ID_OPPOSITION } from '../../section/list/list-opposition';
import { FormikProps } from 'formik';
import { KeySections, KeySubjects } from 'civil-status/constants/keys-subjects-section';
import { MUNICIPALITY_MILANO } from 'civil-status/constants/municipality-milano';
import {
  createCondizionatoIdSentenceRecognition1,
  createCondizionatoIdSentenceRecognition2,
} from 'civil-status/utils/create-condizionato-id-sentence-recognition';

interface FormProps {
  disabled?: boolean;
  onChange: (key: keyof DocumentVM, value: DocumentVM[keyof DocumentVM]) => void;
  onChanges: (values: DocumentVM) => void;
  values?: DocumentVM;
  allegatiUsecaseData?: Array<DocumentWizard>;
  idEvento?: number;
  errorFiles?: boolean;
  formContext: FormikProps<LastStepVM>;
  isStepFromEmergency?: boolean;
}

const FormComponent = ({
  disabled,
  onChange,
  values,
  onChanges,
  allegatiUsecaseData = [],
  idEvento,
  errorFiles = false,
  formContext,
  isStepFromEmergency = false,
}: FormProps): React.ReactElement => {
  const [withError, setWithError] = useState<boolean>(false);
  const [errorToVisualize, setErrorToVisualize] = useState<{
    visibility?: boolean;
    title?: string;
    messages?: Array<ItemVM>;
  }>({
    visibility: false,
    title: '',
    messages: [],
  });
  const insertData = useHttpClient(`${__ACT_API_URL__}/allegato-atti/inserimento`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const deleteData = useHttpClient(``, {
    options: {
      method: 'DELETE',
      loading: true,
      errorCatching: true,
    },
  });

  const saveData = useHttpClient(`${__ACT_API_URL__}/gestione-allegato-atti/invio`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const verifyData = useHttpClient(`${__ACT_API_URL__}/gestione-allegato-atti/verifica`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const valuesNascituro: ParentVM | undefined = useMemo(() => {
    return (formContext.values[KeySubjects.keyIntNas] ||
      formContext.values[KeySubjects.keyIntNas2] ||
      formContext.values[KeySubjects.keyIntNas2C1]) as ParentVM | undefined;
  }, [
    formContext.values[KeySubjects.keyIntNas],
    formContext.values[KeySubjects.keyIntNas2],
    formContext.values[KeySubjects.keyIntNas2C1],
  ]);

  const valuesDatiTrascrizione: SectionVM | undefined = useMemo(() => {
    return formContext.values[KeySections.keyDatiTrascrizione] as SectionVM | undefined;
  }, [formContext.values[KeySections.keyDatiTrascrizione]]);

  const valuesDatiGeneraliCons: SectionVM | undefined = useMemo(() => {
    return (formContext.values[KeySections.keyDatiGeneraliConsAssRico] ||
      formContext.values[KeySections.keyDatiGeneraliAssRico] ||
      formContext.values[KeySections.keyDatiGeneraliPresDoc] ||
      formContext.values[KeySections.keyDatiGeneraliConsRico] ||
      formContext.values[KeySections.keyDatiGeneraliConsAssCognridRico] ||
      formContext.values[KeySections.keyDatiGeneraliConsRicoSent] ||
      formContext.values[KeySections.keyDatiGeneraliRicoGen]) as SectionVM | undefined;
  }, [
    formContext.values[KeySections.keyDatiGeneraliConsAssRico],
    formContext.values[KeySections.keyDatiGeneraliAssRico],
    formContext.values[KeySections.keyDatiGeneraliPresDoc],
    formContext.values[KeySections.keyDatiGeneraliConsRico],
    formContext.values[KeySections.keyDatiGeneraliConsAssCognridRico],
    formContext.values[KeySections.keyDatiGeneraliConsRicoSent],
    formContext.values[KeySections.keyDatiGeneraliRicoGen],
  ]);

  const valuesDatiGeraliAss: SectionVM | undefined = useMemo(() => {
    return formContext.values[KeySections.keyDatiGeneraliAssRicoP] as SectionVM | undefined;
  }, [formContext.values[KeySections.keyDatiGeneraliAssRicoP]]);

  const valuesMatri: SectionVM | undefined = useMemo(() => {
    return (formContext.values[KeySections.keyDatiSpecificiMatr] ||
      formContext.values[KeySections.keyDatiTrascrizioneMatrimonio] ||
      formContext.values[KeySections.keyDatiMatrimonio]) as SectionVM | undefined;
  }, [
    formContext.values[KeySections.keyDatiSpecificiMatr],
    formContext.values[KeySections.keyDatiTrascrizioneMatrimonio],
    formContext.values[KeySections.keyDatiMatrimonio],
  ]);

  const valuesAttoNascitaFiglio: SectionVM | undefined = useMemo(() => {
    return formContext.values[KeySections.keyAttoNascitaFiglio] as SectionVM | undefined;
  }, [formContext.values[KeySections.keyAttoNascitaFiglio]]);

  // In input prende l'item da controllare e tutti i file obbligatori modificandoli e restituendoli
  // la funzione che lo chiama poi setterà i nuovi file obbligatori così da aggiornare lo stato una
  // volta sola
  const obligatoryCheck = useEventCallback(
    // Funzione che gestisce il controllo degli allegati obbligatori
    (currentItem?: DocumentWizard, obligatoryFiles?: Array<string>): Array<string> | undefined => {
      // Verifica se l'elemento corrente è già stato segnalato come errore
      const isAlreadyError = (obligatoryFiles || []).includes(currentItem?.key?.tipoAllegato || '');

      // Se è già stato segnalato come errore, restituisci l'elenco dei file obbligatori senza modificarlo
      if (isAlreadyError) {
        return obligatoryFiles;
      }

      // Se il tipo di presenza dell'elemento corrente è "Obbligatorio", aggiungi il suo tipo di allegato agli obbligatori
      if (currentItem?.tipoPresenza === typePresence.Obbligatorio) {
        const obligatoryFilesCopy: Array<string> = (obligatoryFiles || []).slice();
        obligatoryFilesCopy.push(currentItem?.key?.tipoAllegato || '');
        return obligatoryFilesCopy;
      }

      // Altrimenti, controlla se ci sono tipi di verifiche, luoghi di nascita, richieste o dichiarazioni specificati
      const idVerificationType =
        valuesNascituro?.generality?.verificationTypeItem?.id?.toString() || undefined;
      const idTypeBirthPlace = valuesDatiTrascrizione?.typeBirthPlace || undefined;
      const idTypeRequest =
        valuesDatiTrascrizione?.typeRequest || valuesMatri?.typeRequest || undefined;
      const idTypeDeclaration =
        valuesDatiGeneraliCons?.typeDeclaration ||
        valuesDatiGeraliAss?.typeDeclaration ||
        undefined;
      const idParentRecognitionMultipleControl =
        valuesDatiTrascrizione?.parentRecognition || undefined;
      const idParentRecognition = valuesDatiGeneraliCons?.parentRecognition || undefined;
      const idPublication = valuesMatri?.publication || undefined;
      const idAgeDefect = valuesMatri?.ageDefect || undefined;
      const idRelationship = valuesMatri?.relationship || undefined;
      const idTemporaryBanRemarriage = valuesMatri?.isTemporaryBanRemarriage || undefined; // boolean
      const idOpposition = valuesMatri?.opposition || undefined;
      const isMunicipalityDifferentMilano =
        valuesAttoNascitaFiglio?.actForm?.italian?.municipality?.description !==
        MUNICIPALITY_MILANO.description;

      // Se nessun tipo specificato, restituisci l'elenco dei file obbligatori senza modificarlo
      if (
        !idVerificationType &&
        !idTypeBirthPlace &&
        !idTypeRequest &&
        !idTypeDeclaration &&
        !idParentRecognitionMultipleControl &&
        !idParentRecognition &&
        !idPublication &&
        !idAgeDefect &&
        !idRelationship &&
        !idTemporaryBanRemarriage &&
        !idOpposition &&
        !isMunicipalityDifferentMilano
      ) {
        return obligatoryFiles;
      }

      // Formatta i tipi specificati come condizionati
      const condizionatoIdVerificationType = idVerificationType
        ? typePresence.Condizionato + idVerificationType.padStart(2, '0')
        : undefined;
      const condizionatoIdTypeBirthPlace = idTypeBirthPlace
        ? typePresence.Condizionato + idTypeBirthPlace.padStart(2, '0')
        : undefined;
      const condizionatoIdTypeRequest = idTypeRequest
        ? typePresence.Condizionato + idTypeRequest.padStart(2, '0')
        : undefined;
      const condizionatoIdSentenceRecognition1 = idParentRecognitionMultipleControl
        ? typePresence.Condizionato +
          createCondizionatoIdSentenceRecognition1(idParentRecognitionMultipleControl)
        : undefined;
      const condizionatoIdSentenceRecognition2 = idParentRecognitionMultipleControl
        ? typePresence.Condizionato +
          createCondizionatoIdSentenceRecognition2(idParentRecognitionMultipleControl)
        : undefined;
      const condizionatoIdTypeDeclaration = idTypeDeclaration
        ? typePresence.Condizionato + idTypeDeclaration.padStart(2, '0')
        : undefined;
      const condizionatoIdParentRecognition = idParentRecognition
        ? typePresence.Condizionato + idParentRecognition.padStart(2, '0')
        : undefined;
      const condizionatoIdPublication = idPublication
        ? typePresence.Condizionato + idPublication.padStart(2, '0')
        : undefined;
      const condizionatoIdAgeDefect =
        idAgeDefect && idAgeDefect !== '1'
          ? typePresence.Condizionato + idAgeDefectDefault
          : undefined;
      const condizionatoIdRelationship =
        idRelationship && idRelationship !== '0'
          ? typePresence.Condizionato + idRelationshipDefault
          : undefined;
      const condizionatoIdTemporaryBanRemarriage = idTemporaryBanRemarriage
        ? typePresence.Condizionato + idTemporaryBanRemarriageDefault
        : undefined;
      const condizionatoIdOpposition =
        idOpposition === ID_OPPOSITION.ID_DECRETO
          ? typePresence.Condizionato + idOppositionDefault
          : undefined;
      const condizionatoIdOpposition2 =
        idOpposition === ID_OPPOSITION.ID_PROVVEDIMENTO
          ? typePresence.Condizionato + idOpposition2Default
          : undefined;
      const condizionatoIsMunicipalityDifferentMilano = isMunicipalityDifferentMilano
        ? typePresence.Condizionato + idMunicipalityDifferentMilano
        : undefined;

      // Se il tipo di presenza dell'elemento corrente corrisponde a uno dei tipi specificati,
      // aggiungi il suo tipo di allegato agli obbligatori
      if (
        currentItem?.tipoPresenza === condizionatoIdVerificationType ||
        currentItem?.tipoPresenza === condizionatoIdTypeBirthPlace ||
        currentItem?.tipoPresenza === condizionatoIdTypeRequest ||
        currentItem?.tipoPresenza === condizionatoIdSentenceRecognition1 ||
        currentItem?.tipoPresenza === condizionatoIdSentenceRecognition2 ||
        currentItem?.tipoPresenza === condizionatoIdTypeDeclaration ||
        currentItem?.tipoPresenza === condizionatoIdParentRecognition ||
        currentItem?.tipoPresenza === condizionatoIdPublication ||
        currentItem?.tipoPresenza === condizionatoIdAgeDefect ||
        currentItem?.tipoPresenza === condizionatoIdRelationship ||
        currentItem?.tipoPresenza === condizionatoIdTemporaryBanRemarriage ||
        currentItem?.tipoPresenza === condizionatoIdOpposition ||
        currentItem?.tipoPresenza === condizionatoIdOpposition2 ||
        currentItem?.tipoPresenza === condizionatoIsMunicipalityDifferentMilano
      ) {
        const obligatoryFilesCopy: Array<string> = (obligatoryFiles || []).slice();
        obligatoryFilesCopy.push(currentItem?.key?.tipoAllegato || '');
        return obligatoryFilesCopy;
      }

      // Se non soddisfa nessuna delle condizioni precedenti, restituisci l'elenco dei file obbligatori senza modificarlo
      return obligatoryFiles;
    }
  );

  const obligatoryCheckCurrent = useEventCallback(
    (obligatoryFiles?: Array<string>): Array<string> | undefined => {
      if ((obligatoryFiles || []).length === 0) {
        return [];
      }

      const res: Array<string> = [];

      obligatoryFiles?.forEach((itemObbligatory) => {
        if (values?.files?.find((itemFile) => itemFile.tipoAllegato === itemObbligatory)) {
          // se è presente non aggiungere all'array di controllo
          return;
        }

        res.push(itemObbligatory);
      });

      return res;
    }
  );

  const handleAddFile = useEventCallback((file: File, item: DocumentWizard) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};

      // inserire un record sulla tabella allegato
      const data = {
        idEvento,
        tipoAllegato: item.key?.tipoAllegato || undefined,
        nomeFile: file.name || undefined,
        oggettoAllegato: (result as string)?.split(',')[1],
        descrizioneTipoAllegato: values?.currentDescription || undefined,
      };

      (
        insertData({
          data,
        }) as ResponsePromise<HttpResponse<AttachmentsData>>
      ).then((response) => {
        toast.success('Operazione eseguita con successo');

        const newFiles: Array<TypeFile> = (values?.files || [])?.slice();

        const filesInsert: Array<TypeFile> = [
          {
            ...file,
            raw: result as string,
            idAllegato: response?.data.payload.key?.idAllegato,
            name: file.name || '',
            tipoAllegato: response?.data.payload.tipoAllegato,
            codStato: response?.data.payload.codStato,
          },
        ];

        const obligatoryFilesCurrentCopy: Array<string> = (
          values?.obligatoryFilesCurrent || []
        )?.slice();

        const keyDescription = `description-${item.key?.tipoAllegato}-${file.name}`;

        onChanges({
          ...values,
          descriptions: {
            ...(values?.descriptions || {}),
            [keyDescription]: values?.currentDescription || '',
          },
          currentDescription: undefined,
          files: newFiles.concat(filesInsert),
          typeDocument: undefined,
          obligatoryFilesCurrent: obligatoryFilesCurrentCopy.filter(
            (itemError) => itemError !== item.key?.tipoAllegato
          ), // eliminare dagli errori correnti se presente
        });
      });
    });

    reader.readAsDataURL(file);
  });

  const onTrashPress = useEventCallback((itemCurrent: DocumentWizard, idAllegato?: number) => {
    if (!idAllegato) {
      return;
    }

    (
      deleteData({
        url: `${__ACT_API_URL__}/allegato-atti/cancellazione/${idAllegato}`,
      }) as unknown as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      const newFiles: Array<TypeFile> =
        (values?.files || []).filter((item) => item.idAllegato !== idAllegato) || [];

      const obligatoryFilesCurrentCopy: Array<string> = (
        values?.obligatoryFilesCurrent || []
      )?.slice();

      if (values?.obligatoryFiles?.find((itemFind) => itemFind === itemCurrent.key?.tipoAllegato)) {
        obligatoryFilesCurrentCopy.push(itemCurrent.key?.tipoAllegato || '');
      }

      onChanges({
        ...(values || {}),
        files: newFiles,
        obligatoryFilesCurrent: obligatoryFilesCurrentCopy,
      });
    });
  });

  const onSend = useEventCallback(() => {
    if ((values?.files || [])?.length === 0) {
      // ancora non allegato nessun file
      return;
    }

    if (errorFiles) {
      setWithError(true);
      return;
    } else {
      setWithError((oldState) => (oldState ? false : oldState));
    }

    const data = {
      listaAllegatoAtti: values?.files?.map((item) => {
        return {
          key: {
            idAllegato: item.idAllegato || undefined,
          },
        };
      }),
    };

    (
      saveData({
        data,
      }) as unknown as ResponsePromise<
        HttpResponse<{ listaAllegatoAtti?: Array<AllegatoAtti>; errori?: Array<string> }>,
        HttpResponse<{ listaAllegatoAtti?: Array<AllegatoAtti>; errori?: Array<string> }>
      >
    ).then((response) => {
      toast.success('Operazione eseguita con successo');

      const castedErrori: Array<string> =
        (response as AxiosResponse<HttpResponse<{ errori?: Array<string> }>>).data.payload.errori ||
        [];
      const castedResponse: Array<AllegatoAtti> =
        (response as AxiosResponse<HttpResponse<{ listaAllegatoAtti?: Array<AllegatoAtti> }>>).data
          .payload.listaAllegatoAtti || [];

      const filesCopy: Array<TypeFile> = (values?.files || [])?.slice();

      const newFiles: Array<TypeFile> = filesCopy.map((item) => ({
        ...item,
        codStato:
          castedResponse.find((itemFind) => itemFind.key?.idAllegato === item.idAllegato)
            ?.codStato || item.codStato,
      }));

      onChanges({
        ...(values || {}),
        files: newFiles,
        typeDocument: undefined,
        isSaved: castedErrori.length === 0,
      });

      if (castedErrori.length === 0) {
        setErrorToVisualize({
          title: '',
          visibility: false,
        });
        return;
      }

      const title =
        castedErrori?.length > 1 ? `Ci sono ${castedErrori?.length} errori` : `C'è 1 errore`;
      const messages = castedErrori.map((item, index) => ({
        id: index,
        value: item,
      }));

      setErrorToVisualize({
        title,
        visibility: true,
        messages,
      });
    });
  });

  const onVerify = useEventCallback(() => {
    const data = {
      listaAllegatoAtti: values?.files?.map((item) => {
        return {
          key: {
            idAllegato: item.idAllegato || undefined,
          },
        };
      }),
    };

    (
      verifyData({
        data,
      }) as unknown as ResponsePromise<
        HttpResponse<{ listaAllegatoAtti?: Array<AllegatoAtti> }>,
        HttpResponse<{ listaAllegatoAtti?: Array<AllegatoAtti> }>
      >
    ).then((response) => {
      toast.success('Operazione eseguita con successo');

      const castedResponse: Array<AllegatoAtti> =
        (response as AxiosResponse<HttpResponse<{ listaAllegatoAtti?: Array<AllegatoAtti> }>>).data
          .payload.listaAllegatoAtti || [];

      const filesCopy: Array<TypeFile> = (values?.files || [])?.slice();

      const newFiles: Array<TypeFile> = filesCopy.map((item) => ({
        ...item,
        codStato:
          castedResponse.find((itemFind) => itemFind.key?.idAllegato === item.idAllegato)
            ?.codStato || item.codStato,
      }));

      onChanges({
        ...(values || {}),
        files: newFiles,
        typeDocument: undefined,
      });
    });
  });

  const isVerify = useMemo(() => {
    const res0 = values?.files?.find(
      (item) => item.codStato === idStateDocumentWizard.CaricatoInSide
    );

    return !Boolean(res0);
  }, [values?.files]);

  const disabledVerify = useMemo(() => {
    const res3or4 = values?.files?.filter(
      (item) =>
        item.codStato === idStateDocumentWizard.Inserito ||
        item.codStato === idStateDocumentWizard.Rifiutato
    );

    return (values?.files || [])?.length === (res3or4 || [])?.length;
  }, [values?.files]);

  useEffect(() => {
    if (
      !valuesNascituro &&
      !valuesDatiTrascrizione &&
      !valuesDatiGeneraliCons &&
      !valuesDatiGeraliAss &&
      !valuesMatri &&
      !valuesAttoNascitaFiglio
    ) {
      // se non ci sono i dati del nascituro o dati trascrizione non effettuare controlli per il tipo
      return;
    }

    // annullare i vecchi file obbligatori, perchè cambiato verificationTypeItem cambiano anche gli altri
    onChanges({
      ...values,
      obligatoryFiles: [],
      obligatoryFilesCurrent: [],
    });
    setWithError(false);

    if (
      !valuesNascituro?.generality?.verificationTypeItem?.id &&
      !valuesDatiTrascrizione?.typeBirthPlace &&
      !valuesDatiTrascrizione?.typeRequest &&
      !valuesDatiTrascrizione?.parentRecognition &&
      !valuesDatiGeneraliCons?.typeDeclaration &&
      !valuesDatiGeraliAss?.typeDeclaration &&
      !valuesDatiGeneraliCons?.parentRecognition &&
      !valuesMatri?.publication &&
      !valuesMatri?.ageDefect &&
      !valuesMatri?.relationship &&
      !valuesMatri?.isTemporaryBanRemarriage &&
      !valuesMatri?.opposition &&
      !valuesMatri?.typeRequest &&
      !valuesAttoNascitaFiglio?.actForm?.italian?.municipality
    ) {
      return;
    }

    let obligatoryFilesNew: Array<string> | undefined = [];
    // dipendenza errori da verificationTypeItem del nascituro o trascrizione
    allegatiUsecaseData.forEach((item) => {
      obligatoryFilesNew = obligatoryCheck(item, obligatoryFilesNew);
    });

    onChanges({
      ...values,
      obligatoryFiles: obligatoryFilesNew,
      obligatoryFilesCurrent: obligatoryFilesNew.length
        ? obligatoryCheckCurrent(obligatoryFilesNew)
        : [],
    });
  }, [
    valuesNascituro?.generality?.verificationTypeItem,
    valuesDatiTrascrizione?.typeBirthPlace,
    valuesDatiTrascrizione?.typeRequest,
    valuesDatiTrascrizione?.parentRecognition,
    valuesDatiGeneraliCons?.typeDeclaration,
    valuesDatiGeraliAss?.typeDeclaration,
    valuesDatiGeneraliCons?.parentRecognition,
    valuesMatri?.publication,
    valuesMatri?.ageDefect,
    valuesMatri?.relationship,
    valuesMatri?.isTemporaryBanRemarriage,
    valuesMatri?.opposition,
    valuesMatri?.typeRequest,
    valuesAttoNascitaFiglio?.actForm?.italian?.municipality,
  ]);

  useEffect(() => {
    let obligatoryFilesNew: Array<string> | undefined = (values?.obligatoryFiles || [])?.slice();

    // controllo errori
    allegatiUsecaseData.forEach((item) => {
      obligatoryFilesNew = obligatoryCheck(item, obligatoryFilesNew);
    });

    onChanges({
      ...values,
      obligatoryFiles: obligatoryFilesNew,
      obligatoryFilesCurrent: obligatoryFilesNew.length
        ? obligatoryCheckCurrent(obligatoryFilesNew)
        : [],
    });
  }, [allegatiUsecaseData]);

  useEffect(() => {
    if (
      (values?.files || [])?.length === 0 ||
      (values?.obligatoryFilesCurrent || [])?.length === 0
    ) {
      return;
    }

    // eliminare dai file da inserire quelli già inseriti (utile per quando si ricaricano i file allegati)
    let obligatoryFilesCurrentCopy = (values?.obligatoryFilesCurrent || []).slice();

    let isInError = false;
    values?.files?.forEach((item) => {
      isInError = Boolean(
        obligatoryFilesCurrentCopy.find((itemFilter) => itemFilter === item.tipoAllegato)
      );

      if (isInError) {
        obligatoryFilesCurrentCopy = obligatoryFilesCurrentCopy.filter(
          (itemFilter) => itemFilter !== item.tipoAllegato
        );
      }
    });

    if (isInError) {
      onChange('obligatoryFilesCurrent', obligatoryFilesCurrentCopy);
    }
  }, [values?.files, values?.obligatoryFilesCurrent]);

  const onVisibility = useEventCallback(() => {
    setErrorToVisualize({ visibility: false, title: '', messages: [] });
  });

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12}>
        La dimensione del file non deve superare 10 Mb; il nome del file non deve contenere spazi o
        caratteri speciali; sono ammesse le seguenti tipologie di file: PDF, JPG, TIF, P7M.
      </Grid>
      <Grid item xs={12}>
        <SearchMessages
          type={MessageType.Error}
          title={errorToVisualize.title || ''}
          messages={errorToVisualize.messages || []}
          visibility={errorToVisualize.visibility || false}
          onChangeVisibility={onVisibility}
        />
      </Grid>
      {allegatiUsecaseData.map((item, index) => {
        // file scelto
        const currentFile: TypeFile | undefined =
          values?.files?.find((itemFile) => itemFile.tipoAllegato === item.key?.tipoAllegato) ||
          undefined;

        // se è false ci troviamo nella situazione della descrizione libera ed abbiamo la possibilità di inserire più file
        const withDescription = Boolean(item.descrizioneTipoAllegato);

        return (
          <Fragment key={`${item.key?.codUseCase || ''}-${item.key?.tipoAllegato || ''}-${index}`}>
            <FormData
              disabled={disabled}
              onChange={onChange}
              values={values}
              onChanges={onChanges}
              idEvento={idEvento}
              currentFile={currentFile}
              withDescription={withDescription}
              item={item}
              index={index}
              onTrashPress={onTrashPress}
              handleAddFile={handleAddFile}
              withError={withError}
              isStepFromEmergency={isStepFromEmergency}
            />
          </Fragment>
        );
      })}
      <Grid item xs={12} display="flex" justifyContent="center">
        {isVerify ? (
          <PmiButton
            color="cdmBrandThird"
            variant="outlined"
            onClick={onVerify}
            disabled={disabledVerify}
          >
            Verifica
          </PmiButton>
        ) : (
          <PmiButton
            color="cdmBrandThird"
            variant="outlined"
            onClick={onSend}
            disabled={
              (values?.files || [])?.length === 0 ||
              !Boolean(
                values?.files?.find(
                  (item) => item.codStato === idStateDocumentWizard.CaricatoInSide
                )
              )
            }
          >
            Invia
          </PmiButton>
        )}
      </Grid>
    </Grid>
  );
};

FormComponent.displayName = 'Form - accordion document - form';

const Form = memo(FormComponent);

export default Form;
